import React from "react";
import styled from "styled-components";

const Page = styled.main`
  flex: 1;
  background:
    radial-gradient(circle at top left, rgba(184, 69, 12, 0.04), transparent 34%),
    linear-gradient(180deg, #f7f3eb 0%, #f4efe6 54%, #efe9df 100%);
  color: #1f1d18;
`;

const Shell = styled.div`
  width: min(1080px, calc(100% - 44px));
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 22px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #b8450c;
`;

/* ---------- Hero ---------- */

const Hero = styled.section`
  padding: 84px 0 72px;

  @media (max-width: 768px) {
    padding: 56px 0 48px;
  }
`;

const Headline = styled.h1`
  margin: 0 0 26px;
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: clamp(2.9rem, 7vw, 5.4rem);
  line-height: 0.98;
  font-weight: 600;
  max-width: 15ch;
`;

const Lead = styled.p`
  margin: 0;
  max-width: 60ch;
  font-size: clamp(1.08rem, 1.5vw, 1.28rem);
  line-height: 1.7;
  color: #413b32;
`;

const Rule = styled.div`
  width: 64px;
  height: 2px;
  background: #b8450c;
  margin: 40px 0;
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  margin-top: 40px;
`;

const PrimaryCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0 26px;
  background: #23201c;
  color: #f7f3eb;
  text-decoration: none;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  transition:
    transform 0.2s ease,
    background 0.2s ease;

  &:hover {
    background: #b8450c;
    transform: translateY(-1px);
  }
`;

const SecondaryCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0 26px;
  background: transparent;
  color: #23201c;
  text-decoration: none;
  border: 1.5px solid rgba(35, 32, 28, 0.32);
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  transition:
    transform 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background: #23201c;
    border-color: #23201c;
    color: #f7f3eb;
    transform: translateY(-1px);
  }
`;

const Aside = styled.span`
  font-size: 0.95rem;
  color: #6b6153;
  line-height: 1.6;
`;

/* ---------- Section scaffolding ---------- */

const Section = styled.section`
  padding: 72px 0;
  border-top: 1px solid rgba(35, 32, 28, 0.12);

  @media (max-width: 768px) {
    padding: 52px 0;
  }
`;

const SectionHead = styled.div`
  max-width: 62ch;
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  margin: 14px 0 18px;
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1.05;
  font-weight: 600;
`;

const SectionText = styled.p`
  margin: 0;
  font-size: 1.08rem;
  line-height: 1.72;
  color: #4a4338;
`;

/* ---------- The problem ---------- */

const ProblemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

const Problem = styled.div`
  padding-top: 22px;
  border-top: 2px solid #23201c;

  @media (max-width: 820px) {
    padding: 22px 0;
    border-top: 1px solid rgba(35, 32, 28, 0.14);
  }
`;

const ProblemTitle = styled.h3`
  margin: 0 0 12px;
  font-family: "DM Sans", sans-serif;
  font-size: 1.06rem;
  font-weight: 700;
  letter-spacing: 0.01em;
`;

const ProblemText = styled.p`
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.68;
  color: #57503f;
`;

/* ---------- How it works ---------- */

const Steps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Step = styled.article`
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 24px;
  padding: 30px 0;
  border-bottom: 1px solid rgba(35, 32, 28, 0.12);

  &:first-child {
    border-top: 1px solid rgba(35, 32, 28, 0.12);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const StepNo = styled.div`
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: 2.4rem;
  line-height: 1;
  color: #b8450c;
`;

const StepBody = styled.div``;

const StepTitle = styled.h3`
  margin: 0 0 10px;
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.1;
`;

const StepText = styled.p`
  margin: 0;
  max-width: 66ch;
  font-size: 1.02rem;
  line-height: 1.72;
  color: #4a4338;
`;

/* ---------- Pull quote ---------- */

const Quote = styled.section`
  padding: 96px 0;
  border-top: 1px solid rgba(35, 32, 28, 0.12);
  text-align: center;

  @media (max-width: 768px) {
    padding: 68px 0;
  }
`;

const QuoteText = styled.blockquote`
  margin: 0 auto;
  max-width: 22ch;
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: clamp(2.2rem, 5vw, 3.6rem);
  line-height: 1.08;
  font-weight: 500;
`;

const QuoteMark = styled.span`
  color: #b8450c;
`;

/* ---------- Closing / contact ---------- */

const Close = styled.section`
  padding: 80px 0 100px;
  border-top: 1px solid rgba(35, 32, 28, 0.12);

  @media (max-width: 768px) {
    padding: 56px 0 72px;
  }
`;

const CloseTitle = styled.h2`
  margin: 14px 0 20px;
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: clamp(2rem, 4.4vw, 3.2rem);
  line-height: 1.04;
  font-weight: 600;
  max-width: 18ch;
`;

const STEPS = [
  {
    no: "01",
    title: "Health, modeled as a path — not a line item",
    text:
      "A research-supported model moves each simulated life through intuitive health states — the go-go, slow-go, and no-go years. Health is not just a healthcare bill. It sets how much a dollar of discretionary spending is actually worth, and when.",
  },
  {
    no: "02",
    title: "Markets that switch regimes, not a single average",
    text:
      "Rather than one blended return assumption, a regime-aware model transitions between the macroeconomic environments retirements actually pass through. The market path and the health path are simulated together, because in a real life they are lived together.",
  },
  {
    no: "03",
    title: "A spending engine that adapts, like a retiree does",
    text:
      "Burkett's Demon runs each life against each market path, spending across essential, comfort, experiential, and bequest priorities — and adjusting as the portfolio moves, the way real retirees do. It records the comfort of every year lived.",
  },
  {
    no: "04",
    title: "Comfort you can read — the ELCI",
    text:
      "Thousands of lives roll up into a matrix of health and wealth outcomes, then into a single figure: the Expected Lifetime Comfort Index. Not a pass-fail probability — a picture of how well the years were actually lived, and where they fell short.",
  },
];

const PROBLEMS = [
  {
    title: "Flat spending assumptions",
    text:
      "Most tools assume you spend the same, inflation-adjusted, every year for thirty years. Almost no one does. Energy, health, and desire all change — and the plan should know it.",
  },
  {
    title: "Hidden capital-market bets",
    text:
      "A single return-and-volatility input quietly decides the whole answer. The assumptions that matter most are the ones you never see and cannot question.",
  },
  {
    title: "A pass-fail verdict",
    text:
      "“87% chance of success” tells you neither why a plan fails nor what failure would feel like. It is not explainable, and it is not something you can act on.",
  },
];

function Home() {
  return (
    <Page>
      {/* Hero */}
      <Hero>
        <Shell>
          <Kicker>Precipex &middot; Retirement Planning</Kicker>
          <Headline>Retirement planning for the life you can still live.</Headline>
          <Lead>
            The question in retirement is not only whether your money lasts. It is whether you spend it in the right
            places, at the right times, while health and opportunity still line up. Our first tool answers that
            question with a single score: ELCI, the Expected Lifetime Comfort Index &mdash; live now at elci.life.
          </Lead>
          <Rule />
          <Lead>
            We model the two things that actually decide a retirement &mdash; how markets move and how health changes
            &mdash; and measure the outcome in lifetime comfort, not a single probability of success.
          </Lead>
          <CtaRow>
            <PrimaryCta href="https://elci.life" target="_blank" rel="noopener noreferrer">
              Try ELCI at elci.life
            </PrimaryCta>
            <SecondaryCta href="mailto:info@precipex.com?subject=Precipex">Get in touch</SecondaryCta>
            <Aside>A 0&ndash;100 score for how comfortably your plan actually gets lived. Now in beta.</Aside>
          </CtaRow>
        </Shell>
      </Hero>

      {/* The problem */}
      <Section>
        <Shell>
          <SectionHead>
            <Kicker as="p">The gap</Kicker>
            <SectionTitle>The prepared retiree is served worst by today&apos;s tools.</SectionTitle>
            <SectionText>
              The people who saved diligently are pushed toward chronic underspending &mdash; holding back exactly when
              timing, energy, and health still line up. The software meant to reassure them treats a whole life as a
              spreadsheet that either passes or fails.
            </SectionText>
          </SectionHead>
          <ProblemGrid>
            {PROBLEMS.map((p) => (
              <Problem key={p.title}>
                <ProblemTitle>{p.title}</ProblemTitle>
                <ProblemText>{p.text}</ProblemText>
              </Problem>
            ))}
          </ProblemGrid>
        </Shell>
      </Section>

      {/* How it works */}
      <Section>
        <Shell>
          <SectionHead>
            <Kicker as="p">The approach</Kicker>
            <SectionTitle>Two chains, one life, measured in comfort.</SectionTitle>
            <SectionText>
              Underneath a deliberately simple surface, Precipex couples market dynamics with health-state transitions
              and runs a life thousands of times over. Here is what happens between the question and the answer.
            </SectionText>
          </SectionHead>
          <Steps>
            {STEPS.map((s) => (
              <Step key={s.no}>
                <StepNo>{s.no}</StepNo>
                <StepBody>
                  <StepTitle>{s.title}</StepTitle>
                  <StepText>{s.text}</StepText>
                </StepBody>
              </Step>
            ))}
          </Steps>
        </Shell>
      </Section>

      {/* Pull quote */}
      <Quote>
        <Shell>
          <QuoteText>
            Comfort is a distribution
            <QuoteMark>,</QuoteMark>
            {" "}
            not a probability.
          </QuoteText>
        </Shell>
      </Quote>

      {/* Close */}
      <Close>
        <Shell>
          <Kicker as="p">Talk to us</Kicker>
          <CloseTitle>If any of this is how you already think about retirement, we should talk.</CloseTitle>
          <SectionText>
            ELCI is live at elci.life, and we&apos;re building it for advisors and self-directed retirees who want to
            plan for a life well lived, not just a balance that survives. Run your plan through it &mdash; then tell
            us what it got right and what it missed. Early conversations shape what we build next.
          </SectionText>
          <CtaRow>
            <PrimaryCta href="https://elci.life" target="_blank" rel="noopener noreferrer">
              Try ELCI
            </PrimaryCta>
            <SecondaryCta href="mailto:info@precipex.com?subject=Precipex">Contact Precipex</SecondaryCta>
          </CtaRow>
        </Shell>
      </Close>
    </Page>
  );
}

export default Home;
